import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { Banknote, ChevronRight, CreditCard, Download, FileText } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_FLIGHTS, DEMO_TRANSACTIONS, DEMO_UIDS } from '../../lib/demoData';
import { fmtHours, fmtUSD, pastCycles, rateFor, summarizeCurrentCycle } from '../../lib/billingCycles';
import { downloadReceiptPdf, downloadStatementPdf, downloadLeasePdf } from '../../lib/pdf';
import type { AircraftDoc, FlightDoc, LeaseSignatureDoc, TransactionDoc } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { PaymentCardStep, useCardReturn } from '../../components/PaymentCardStep';
import { OWNER_FIRST } from '../../lib/identity';
import { TRACKED_REG } from '../../lib/flightTrack';

export function Billing({ preview }: { preview?: boolean } = {}) {
  const { profile, fbUser, demoMode } = useAuth();
  // Finalize a card saved via Stripe's hosted page (redirect returns with ?card=saved),
  // and surface the outcome (saved / failed / cancelled) to the pilot.
  const cardStatus = useCardReturn();

  const [flights, setFlights] = useState<FlightDoc[]>([]);
  // The pilot's own ledger (server-written; rules allow read-own). This is where a
  // cash payment lives — flights alone can't tell the pilot whether they've paid.
  const [transactions, setTransactions] = useState<TransactionDoc[]>([]);
  const [aircraft, setAircraft] = useState<AircraftDoc | null>(null);
  const [updatingCard, setUpdatingCard] = useState(false);
  const [leaseSig, setLeaseSig] = useState<LeaseSignatureDoc | null>(null);

  // The pilot's executed lease (for the downloadable copy).
  useEffect(() => {
    if (preview || demoMode || !db || !fbUser) return;
    getDocs(query(collection(db, 'leaseSignatures'), where('signerUid', '==', fbUser.uid)))
      .then((snap) => {
        const sigs = snap.docs.map((d) => d.data() as LeaseSignatureDoc);
        sigs.sort((a, b) => (b.signedAt?.toMillis?.() ?? 0) - (a.signedAt?.toMillis?.() ?? 0));
        setLeaseSig(sigs[0] ?? null);
      })
      .catch(() => {});
  }, [preview, demoMode, fbUser]);

  useEffect(() => {
    if (preview) {
      const now = new Date();
      const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setAircraft(DEMO_AIRCRAFT[0]);
      setFlights([
        { flightId: 'p1', date: `${ym}-05`, hobbsOut: 4220.0, hobbsIn: 4226.5, hoursFlown: 6.5, departureAirport: 'KLGB', destinations: ['KSBA', 'KLGB'], awayFromBaseCharge: 0, totalCharge: 650 },
        { flightId: 'p2', date: `${ym}-15`, hobbsOut: 4226.5, hobbsIn: 4233.3, hoursFlown: 6.8, departureAirport: 'KLGB', destinations: ['KSEE'], awayFromBaseCharge: 200, totalCharge: 880 },
      ] as unknown as FlightDoc[]);
      setTransactions([
        { transactionId: 'cash_p1', lesseeUid: 'p', aircraftId: TRACKED_REG, flightId: 'p1', type: 'flight_charge', amount: 650, status: 'succeeded', paymentMethod: 'cash', description: 'Cash — 6.5 hr x $100.00/hr' },
        { transactionId: 't_p2', lesseeUid: 'p', aircraftId: TRACKED_REG, flightId: 'p2', type: 'flight_charge', amount: 880, status: 'succeeded', paymentMethod: 'card', description: '6.8 hr x $100.00/hr + away-from-base' },
      ] as unknown as TransactionDoc[]);
      return;
    }
    if (demoMode) {
      const uid = profile?.status === 'active' ? DEMO_UIDS.brian : profile?.uid ?? '';
      setFlights(DEMO_FLIGHTS.filter((f) => f.lesseeUid === uid));
      setTransactions(DEMO_TRANSACTIONS.filter((t) => t.lesseeUid === uid));
      setAircraft(DEMO_AIRCRAFT[0]);
      return;
    }
    if (!db || !fbUser) return;
    const unsubF = onSnapshot(
      query(collection(db, 'flights'), where('lesseeUid', '==', fbUser.uid)),
      (snap) => setFlights(snap.docs.map((d) => d.data() as FlightDoc))
    );
    const unsubT = onSnapshot(
      query(collection(db, 'transactions'), where('lesseeUid', '==', fbUser.uid)),
      (snap) => setTransactions(snap.docs.map((d) => ({ ...(d.data() as TransactionDoc), transactionId: d.id })))
    );
    const unsubA = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft((snap.docs[0]?.data() as AircraftDoc) ?? null)
    );
    return () => {
      unsubF();
      unsubT();
      unsubA();
    };
  }, [preview, demoMode, fbUser, profile?.status, profile?.uid]);

  const billingDay = profile?.billingDayOfMonth ?? 1;
  // The pilot's OWN rate (promo/CFI = $75, standard = $100) — not the aircraft's
  // standard rate. Carter saw "$100/hr" here on 10/22 while being billed $75.
  const rate = aircraft ? rateFor(profile, aircraft) : 0;
  const current = useMemo(
    () => (aircraft ? summarizeCurrentCycle({ flights, aircraft, billingDayOfMonth: billingDay, rate }) : null),
    [flights, aircraft, billingDay, rate]
  );
  const past = useMemo(
    () => (aircraft ? pastCycles({ flights, aircraft, billingDayOfMonth: billingDay, count: 6, rate }) : []),
    [flights, aircraft, billingDay, rate]
  );

  // Payments: every ledger entry, newest first. Cash owed is the only "balance" a
  // pilot can carry here — card flights are charged the moment they're logged.
  const txDate = (t: TransactionDoc) => t.cashReceivedAt?.toDate?.() ?? t.createdAt?.toDate?.() ?? new Date(0);
  const ledger = useMemo(
    () =>
      transactions
        .filter((t) => t.type !== 'grounding_credit')
        .sort((a, b) => txDate(b).getTime() - txDate(a).getTime()),
    [transactions]
  );
  const cashOwed = useMemo(
    () => ledger.filter((t) => t.status === 'cash_due').reduce((s, t) => s + (t.amount || 0), 0),
    [ledger]
  );
  const failedOwed = useMemo(
    () => ledger.filter((t) => t.status === 'failed').reduce((s, t) => s + (t.amount || 0), 0),
    [ledger]
  );
  const flightById = useMemo(() => new Map(flights.map((f) => [f.flightId, f])), [flights]);
  const isCashTx = (t: TransactionDoc) =>
    t.paymentMethod === 'cash' || t.status === 'cash_due' || t.transactionId.startsWith('cash_');

  if (!aircraft || !current) {
    return (
      <>
        <PageHeader title="Billing" subtitle="Your plan, this month, and statements." />
        <div className="card p-8 text-center text-ink-muted">Loading your billing…</div>
      </>
    );
  }

  // Comped plan (owner's guest): flights are recorded but never billed.
  const comped = profile?.plan === 'comped';
  const cardLine = preview
    ? 'VISA ····4242'
    : profile?.paymentMethodOnFile
    ? `${(profile.cardBrand ?? 'Card').toUpperCase()} ····${profile.cardLast4 ?? ''}`
    : 'No card on file';
  const nextCharge = current.end.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  const noCardOnFile = !preview && !demoMode && !profile?.paymentMethodOnFile;

  return (
    <>
      <PageHeader title="Billing" subtitle="Your plan, this month, and statements." />

      {/* Result of returning from Stripe's hosted card page — always tell the user
          plainly whether the card actually saved. */}
      {cardStatus === 'saved' && (
        <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          ✓ Your card was saved. You’re all set — flights will be charged to it automatically.
        </div>
      )}
      {cardStatus === 'failed' && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          <strong>That didn’t work — no card was saved.</strong> Please tap “Add a card” below and try
          again. If it keeps failing, use a different card or reach out to AJM.
        </div>
      )}
      {cardStatus === 'cancelled' && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Card setup was cancelled — <strong>no card is on file yet.</strong> You’ll need one before your
          first flight; tap “Add a card” below when you’re ready.
        </div>
      )}

      {/* Clear standing warning whenever there's genuinely no card on file.
          Comped pilots are never charged, so no card is needed. */}
      {noCardOnFile && cardStatus === 'idle' && !comped && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>No payment card on file.</strong> Add one before you fly — flights can’t be charged
          without a card, and any flown time stays owed until one is on file.
        </div>
      )}

      {/* Monthly plan */}
      <div className="card overflow-hidden">
        <div className="bg-brand px-5 py-4 text-white sm:px-6">
          <div className="text-xs uppercase tracking-widest text-sky-100">Your monthly plan</div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {comped ? (
              <>
                <span className="font-display text-3xl font-bold">Comped</span>
                <span className="text-sky-100">· your flights are complimentary</span>
              </>
            ) : (
              <>
                <span className="font-display text-3xl font-bold">{fmtUSD(current.rate)}</span>
                <span className="text-sky-100">/ Hobbs hour</span>
                <span className="text-sm text-sky-100">· no monthly minimum</span>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 px-5 py-4 text-sm sm:grid-cols-3 sm:px-6">
          <Row label="Next statement" value={nextCharge} />
          <Row label="Hourly rate" value={comped ? '$0 — comped' : `${fmtUSD(current.rate)} / hr`} />
          <Row label="Card on file" value={cardLine} icon />
        </div>
        <p className="border-t border-slate-100 px-5 py-3 text-xs text-ink-muted sm:px-6">
          {comped
            ? 'Your flights are comped by AJM — nothing is ever billed. Hobbs/Tach are still recorded on every flight so the aircraft’s time stays continuous.'
            : `There is no monthly minimum. You pay only for the Hobbs hours you fly at ${fmtUSD(current.rate)}/hr, charged per flight, plus two hours for each night ${TRACKED_REG} stays away from Long Beach. The rate can change on 30 days' written notice, never retroactively (lease §5.7).`}
        </p>
      </div>

      {/* Update / fix card on file */}
      {!preview && (
        <div className="mt-4">
          {profile?.paymentPastDue && (
            <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              Your account is past due and flying is paused. Update your card below to restore access —
              we’ll retry the outstanding charge automatically.
            </div>
          )}
          {!updatingCard ? (
            <button type="button" className="btn-secondary" onClick={() => setUpdatingCard(true)}>
              {profile?.paymentMethodOnFile ? 'Update card on file' : 'Add a card'}
            </button>
          ) : (
            <div>
              <PaymentCardStep demoMode={!!demoMode} savedCard={null} onSaved={() => setUpdatingCard(false)} />
              <button
                type="button"
                className="mt-2 text-sm text-ink-muted underline"
                onClick={() => setUpdatingCard(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Downloads */}
      {!preview && (
        <div className="card mt-4 p-5">
          <h2 className="font-display text-base font-semibold text-navy-900">Downloads</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              onClick={() =>
                aircraft &&
                downloadStatementPdf({
                  cycle: current,
                  tailNumber: aircraft.tailNumber,
                  pilotName:
                    `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim() ||
                    profile?.email ||
                    'Pilot',
                  cardLine,
                })
              }
            >
              <Download size={14} /> This month’s statement (PDF)
            </button>
          </div>
        </div>
      )}

      {/* Payments — the ledger. Answers "did my cash payment go through?" and
          "do I owe anything?" without the pilot having to text Casey. */}
      <div className="card mt-6 p-5 sm:p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted">Payments</div>
            <h2 className="font-display text-xl font-semibold text-navy-900">
              {cashOwed > 0
                ? `${fmtUSD(cashOwed)} cash owed`
                : failedOwed > 0
                ? `${fmtUSD(failedOwed)} outstanding`
                : 'Nothing owed'}
            </h2>
          </div>
          {cashOwed > 0 ? (
            <div className="text-sm text-amber-800">Hand it to {OWNER_FIRST} within 5 days of the flight</div>
          ) : failedOwed > 0 ? (
            <div className="text-sm text-red-700">A card charge didn’t go through</div>
          ) : (
            <div className="text-sm text-emerald-700">You’re paid up</div>
          )}
        </div>

        {cashOwed > 0 && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>You chose to pay in cash.</strong> Nothing has been charged to a card. Give {OWNER_FIRST} the
            cash within 5 days of the flight and it will show here as paid, with a receipt. After 5 days the
            card on file can be charged for it (lease §5.6).
          </div>
        )}

        {ledger.length === 0 ? (
          <p className="mt-4 text-sm text-ink-muted">No payments yet. Your first flight will show here.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {ledger.map((t) => {
              const cash = isCashTx(t);
              const f = t.flightId ? flightById.get(t.flightId) : undefined;
              const when = txDate(t);
              const paid = t.status === 'succeeded';
              const status =
                t.status === 'cash_due'
                  ? { label: 'Cash owed', cls: 'bg-amber-100 text-amber-800' }
                  : t.status === 'succeeded'
                  ? { label: cash ? 'Paid in cash' : 'Paid', cls: 'bg-emerald-100 text-emerald-800' }
                  : t.status === 'failed'
                  ? { label: 'Card declined', cls: 'bg-red-100 text-red-800' }
                  : t.status === 'refunded'
                  ? { label: 'Refunded', cls: 'bg-slate-100 text-slate-700' }
                  : { label: t.status, cls: 'bg-slate-100 text-slate-700' };
              return (
                <li key={t.transactionId} className="flex items-start justify-between gap-3 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-navy-900">
                        {f ? `${f.date} · ${[f.departureAirport, ...(f.destinations ?? [])].join(' → ')}` : t.description || 'Charge'}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.cls}`}>{status.label}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-muted">
                      <span className="inline-flex items-center gap-1">
                        {cash ? <Banknote size={12} /> : <CreditCard size={12} />}
                        {cash ? 'Cash' : 'Card'}
                      </span>
                      {f && <span>· {fmtHours(f.hoursFlown)} × {fmtUSD(current.rate)}/hr</span>}
                      {paid && when.getTime() > 0 && (
                        <span>
                          · {cash ? 'received' : 'charged'}{' '}
                          {when.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-medium text-navy-900">{fmtUSD(t.amount)}</span>
                    {paid && !preview && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-navy-700 underline"
                        onClick={() =>
                          downloadReceiptPdf({
                            tx: t,
                            flight: f ?? null,
                            payer: {
                              firstName: profile?.personal?.firstName,
                              lastName: profile?.personal?.lastName,
                              email: profile?.email,
                              phone: profile?.personal?.phone,
                              address: profile?.personal?.address,
                              city: profile?.personal?.city,
                              state: profile?.personal?.state,
                              zip: profile?.personal?.zip,
                              leaseSignedAt: profile?.leaseSignedAt?.toDate?.() ?? null,
                            },
                            aircraft: { tailNumber: aircraft.tailNumber, makeModel: [aircraft.year, aircraft.make, aircraft.model].filter(Boolean).join(" "), hourlyRate: aircraft.hourlyRate },
                            cardLine: cash ? undefined : cardLine,
                          })
                        }
                      >
                        <Download size={12} /> Receipt
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* This month */}
      <div className="card mt-6 p-5 sm:p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted">This month</div>
            <h2 className="font-display text-xl font-semibold text-navy-900">
              {current.start.toLocaleDateString([], { month: 'short', day: 'numeric' })} —{' '}
              {current.end.toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </h2>
          </div>
          <div className="text-sm text-ink-muted">
            {current.daysRemaining} of {current.totalDays} days left
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Hobbs hours flown this month</span>
            <span className="font-medium text-navy-900">{fmtHours(current.hoursFlown)}</span>
          </div>
        </div>

        <dl className="mt-5 divide-y divide-slate-100 text-sm">
          <Line
            k={`Hobbs hours — ${fmtHours(current.hoursFlown)} × ${fmtUSD(current.rate)}`}
            v={fmtUSD(current.hoursCharge)}
          />
          <Line k="Away-from-base charges" v={fmtUSD(current.awayCharge)} />
          <div className="flex items-center justify-between pt-3">
            <dt className="font-display text-base font-semibold text-navy-900">Total this month</dt>
            <dd className="font-display text-xl font-bold text-emerald-700">
              {fmtUSD(current.totalCharged)}
            </dd>
          </div>
        </dl>

        {current.flights.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 text-xs uppercase tracking-wider text-ink-muted">
              Flights this month
            </div>
            <ul className="divide-y divide-slate-100">
              {current.flights.map((f) => (
                <li key={f.flightId} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="text-ink">
                      {f.date} · {f.departureAirport} → {f.destinations.join(' → ')}
                    </div>
                    <div className="text-xs text-ink-muted">
                      Hobbs {f.hobbsOut.toFixed(1)}→{f.hobbsIn.toFixed(1)}
                    </div>
                  </div>
                  <div className="font-medium text-ink">{fmtHours(f.hoursFlown)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Past statements */}
      <div className="mt-6">
        <h2 className="mb-3 font-display text-lg font-semibold text-navy-900">Past statements</h2>
        {past.length === 0 ? (
          <div className="card p-6 text-center text-ink-muted">No past months yet.</div>
        ) : (
          <div className="card divide-y divide-slate-100 overflow-hidden">
            {past.map((c) => (
              <Link
                key={c.cycleId}
                to={`/billing/${c.cycleId}`}
                className="flex items-center justify-between gap-3 px-4 py-3.5 transition hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy-900">
                    {new Date(`${c.startDate}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' })} —{' '}
                    {new Date(`${c.endDate}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div className="text-xs text-ink-muted">{fmtHours(c.hoursFlown)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-navy-900">{fmtUSD(c.totalCharges)}</span>
                  <ChevronRight size={16} className="text-ink-subtle" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Row({ label, value, icon }: { label: string; value: string; icon?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5 font-medium text-navy-900">
        {icon && <CreditCard size={14} className="text-ink-muted" />}
        {value}
      </div>
    </div>
  );
}

function Line({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between py-2">
      <dt className="text-ink-muted">{k}</dt>
      <dd className="font-medium text-navy-900">{v}</dd>
    </div>
  );
}
